
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import EditFrame from "../EditFrame.vue";
import EditActionButtons from "../EditActionButtons.vue";
import { deepClone } from "@/misc/helpers";
import { StationLocation } from "@/api/dtos/StationLocation.schema";
import { countries } from "@/misc/countries";
import LocationMapPicker from "./LocationMapPicker.vue";

type MapLocationPayload = {
  lat: number | null;
  lng: number | null;
  address?: string;
  addressDetails?: Record<string, any> | null;
};

@Component({
  components: {
    EditFrame,
    EditActionButtons,
    LocationMapPicker,
  },
})
export default class StationLocationForm extends Vue {
  @Prop()
  readonly stationLocation!: StationLocation;

  @Prop({
    default: false,
  })
  hideControls!: boolean;

  @Prop({
    default: true,
  })
  withImage!: boolean;

  @Prop({
    default: false,
  })
  readonly forceEditing!: boolean;

  editedStationLocation: StationLocation | null = null;
  formValidated = false;
  isEditing = false;

  countries: {
    id: number;
    name: string;
    alpha2: string;
    alpha3: string;
  } = require("@/misc/countries_german.json");

  $refs!: {
    editFrame: EditFrame;
  };

  get countrySelectItems(): { value: string; text: string }[] {
    return countries.map((c) => {
      return {
        value: c.alpha2.toUpperCase(),
        text: c.name,
      };
    });
  }

  get mapLocation(): { lat: number | null; lng: number | null; address: string } {
    if (!this.editedStationLocation) {
      return {
        lat: null,
        lng: null,
        address: "",
      };
    }

    return {
      lat:
        typeof this.editedStationLocation.lat === "number"
          ? this.editedStationLocation.lat
          : null,
      lng:
        typeof this.editedStationLocation.lon === "number"
          ? this.editedStationLocation.lon
          : null,
      address: this.fullAddress,
    };
  }

  get fullAddress(): string {
    if (!this.editedStationLocation || !this.editedStationLocation.address) {
      return "";
    }

    const address = this.editedStationLocation.address;

    return [
      address.street,
      [address.postalCode, address.city].filter(Boolean).join(" "),
      address.country,
    ]
      .filter(Boolean)
      .join(", ");
  }

  @Watch("stationLocation", { deep: true })
  onStationLocationChanged(): void {
    this.initializeWidget();
  }

  mounted(): void {
    this.initializeWidget();

    if (this.forceEditing) {
      this.isEditing = true;
    }
  }

  initializeWidget(): void {
    this.editedStationLocation = deepClone(this.stationLocation);
  }

  onSave(): void {
    this.$emit("save", this.editedStationLocation);
  }

  onCancel(): void {
    this.editedStationLocation = deepClone(this.stationLocation);
  }

  onValidChanged(value: boolean): void {
    this.$emit("valid", value);
    this.formValidated = value;
  }

  tryValidate(): boolean {
    if (this.formValidated) {
      this.onSave();
      return true;
    }

    return false;
  }

  onLocationChange(value: MapLocationPayload): void {
    if (!this.editedStationLocation) return;

    this.editedStationLocation.lat =
      typeof value.lat === "number" ? value.lat : this.editedStationLocation.lat;
    this.editedStationLocation.lon =
      typeof value.lng === "number" ? value.lng : this.editedStationLocation.lon;

    if (value.addressDetails) {
      const a = value.addressDetails;

      const street = [a.road, a.house_number].filter(Boolean).join(" ").trim();
      if (street) {
        this.editedStationLocation.address.street = street;
      }

      if (a.postcode) {
        this.editedStationLocation.address.postalCode = a.postcode;
      }

      const city =
        a.city ||
        a.town ||
        a.village ||
        a.municipality ||
        a.hamlet ||
        a.suburb ||
        "";

      if (city) {
        this.editedStationLocation.address.city = city;
      }

      if (a.country_code) {
        this.editedStationLocation.address.country = String(a.country_code).toUpperCase();
      }
    }
  }

  onLocationError(error: unknown): void {
    console.error("Location picker error:", error);
    this.$emit("error", error);
  }
}
